import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import Spinner from '@/components/spinner';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-normal ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-gray-400 disabled:text-white disabled:hover:bg-gray-200 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-brand-800 has-[button[data-state="open"]]:!bg-brand-800',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-red-800 has-[button[data-state="open"]]:!bg-red-800',
        outline:
          'border border-gray-200 bg-background hover:bg-accent hover:text-accent-foreground has-[button[data-state="open"]]:!bg-accent',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-gray-300 has-[button[data-state="open"]]:!bg-gray-300',
        ghost:
          'hover:bg-accent hover:text-accent-foreground has-[button[data-state="open"]]:!bg-accent disabled:!bg-accent disabled:!text-accent-foreground/50',
        link: 'text-primary underline-offset-4 hover:underline has-[button[data-state="open"]]:underline',
      },
      size: {
        default: 'h-10 px-3 py-2',
        sm: 'h-9 px-3',
        xs: 'h-7 px-2.5 py-1 text-xs',
        lg: 'h-11 px-8',
        icon: 'size-7 [&_svg]:size-[14px]',
        'icon-md': 'size-[38px] [&_svg]:size-[18px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    ref?: React.Ref<HTMLButtonElement>;
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  isLoading,
  loadingText,
  children,
  disabled,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center gap-2">
          <Spinner small className="text-current" />
          {loadingText || children}
        </span>
      ) : (
        children
      )}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
